/**
 * API-hibák emberi, magyar szöveggé alakítása.
 *
 * A DRF validációs hibái `{mező: [üzenet, …]}` alakúak — ezeket mezőnként
 * egy-egy sorba tesszük, magyar mezőnévvel. A Django HTML hibalapját
 * (500-as debug oldal) soha nem mutatjuk meg nyersen.
 */

export const GENERIC_SERVER_ERROR = 'Szerverhiba történt, próbáld újra később.';
export const NETWORK_ERROR = 'Nem sikerült elérni a szervert — ellenőrizd a kapcsolatot, és próbáld újra.';
export const UNKNOWN_ERROR = 'Ismeretlen hiba történt.';

/** Gyakori termék/kategória/variáns mezők magyar címkéi. */
export const DEFAULT_FIELD_LABELS: Record<string, string> = {
  name: 'Név',
  slug: 'Slug',
  description: 'Leírás',
  detailed_description: 'Részletes leírás',
  care_instructions: 'Gondozási útmutató',
  category: 'Kategória',
  product_type: 'Termék típus',
  supplier: 'Beszállító',
  base_price: 'Nettó ár',
  vat_rate: 'ÁFA kulcs',
  stock_quantity: 'Készlet',
  is_active: 'Aktív',
  is_featured: 'Kiemelt',
  sale_price: 'Akciós ár',
  sale_start_date: 'Akció kezdete',
  sale_end_date: 'Akció vége',
  parent: 'Szülő kategória',
  display_order: 'Megjelenítési sorrend',
  image: 'Kép',
  price_modifier: 'Ár módosító',
  sale_price_modifier: 'Akciós ár',
  sku: 'SKU',
  color: 'Szín',
  size: 'Méret',
  gender: 'Fazon',
};

const NO_LABEL_KEYS = new Set(['detail', 'error', 'non_field_errors', 'message']);

const looksLikeHtml = (text: string): boolean => /<\s*(!doctype|html|head|body)\b/i.test(text);

/** Egy mező hibaüzenet(ei) egy szövegként (tömb, string, beágyazott objektum). */
const flattenMessages = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(([key, inner]) => {
      const msgs = flattenMessages(inner);
      return NO_LABEL_KEYS.has(key) ? msgs : msgs.map((m) => `${key}: ${m}`);
    });
  }
  return [];
};

interface ErrorLike {
  response?: { status?: number; data?: unknown };
  request?: unknown;
  message?: string;
}

const asErrorLike = (error: unknown): ErrorLike =>
  (typeof error === 'object' && error !== null ? error : {}) as ErrorLike;

/**
 * Az API-hiba magyar, felhasználónak szánt szövege — mezőnként egy sor.
 *
 * @param error  axios hiba (vagy bármi, amit a catch kapott)
 * @param fieldLabels  extra / felülíró mezőcímkék (pl. `{ name: 'Kategória neve' }`)
 */
export const formatApiError = (error: unknown, fieldLabels: Record<string, string> = {}): string => {
  const err = asErrorLike(error);
  const labels = { ...DEFAULT_FIELD_LABELS, ...fieldLabels };
  const response = err.response;

  if (!response) {
    // Kérés elment, de válasz nem jött → hálózati hiba.
    if (err.request) return NETWORK_ERROR;
    return err.message && !looksLikeHtml(err.message) ? err.message : UNKNOWN_ERROR;
  }

  const { status = 0, data } = response;

  if (typeof data === 'string') {
    const text = data.trim();
    if (!text || looksLikeHtml(text) || status >= 500) return GENERIC_SERVER_ERROR;
    return text;
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const lines = Object.entries(data as Record<string, unknown>)
      // field_errors: mezőnkénti hibák a mező mellé (az `error` már összefoglalja őket).
      .filter(([key]) => key !== 'code' && key !== 'status' && key !== 'field_errors')
      .flatMap(([key, value]) => {
        const msgs = flattenMessages(value);
        if (msgs.length === 0) return [];
        if (NO_LABEL_KEYS.has(key)) return msgs;
        if (typeof value === 'number' || typeof value === 'boolean') return [];
        return [`${labels[key] ?? key}: ${msgs.join(' ')}`];
      });
    if (lines.length > 0) return lines.join('\n');
  }

  if (Array.isArray(data)) {
    const msgs = flattenMessages(data);
    if (msgs.length > 0) return msgs.join('\n');
  }

  if (status >= 500) return GENERIC_SERVER_ERROR;
  return UNKNOWN_ERROR;
};

/**
 * Blob-válaszú kérések (pl. PDF-letöltés, `responseType: 'blob'`) hibájánál a törzs
 * egy Blob — előbb JSON-ná/szöveggé alakítjuk, hogy a formatApiError ki tudja olvasni.
 */
const readBlobText = (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') return blob.text();
  // Régebbi környezet (pl. jsdom): FileReader.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

export const parseBlobErrorBody = async (error: unknown): Promise<unknown> => {
  const err = asErrorLike(error);
  const data = err.response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return error;
  let parsed: unknown = '';
  try {
    const text = await readBlobText(data);
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  } catch {
    parsed = '';
  }
  return { ...err, response: { ...err.response, data: parsed } };
};

/** formatApiError, Blob-törzsű hibákra is (aszinkron). */
export const formatApiErrorAsync = async (
  error: unknown,
  fieldLabels: Record<string, string> = {},
): Promise<string> => formatApiError(await parseBlobErrorBody(error), fieldLabels);

export default formatApiError;
